import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatActionsSidebar from '../components/ChatActionsSidebar';
import { API_ENDPOINTS } from '../config/constants';
import '../components/SettingsPage.css'; // Estilos das abas
import './HaloAgentsPage.css';
import AppIcon from '../components/icons/AppIcon';

/**
 * HaloAgentsPage - Página de chat com agentes IA
 * Layout: Sidebar com histórico | Área central com cards de agentes ou chat ativo
 * Abas superiores para múltiplas conversas abertas
 * 
 * IMPORTANTE: Esta página é exibida no menu como "Meus Agentes" (label invertido)
 * Rota: /agentes-halo
 * Label no menu: "Meus Agentes"
 * Função: CHAT/CONVERSAÇÃO com os agentes
 */
export default function HaloAgentsPage() {
  const { user } = useAuth();

  // ============================================================================
  // ESTADOS
  // ============================================================================
  
  // Agentes disponíveis
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  // Conversas (histórico)
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);

  // Abas abertas (conversas ativas)
  const [openTabs, setOpenTabs] = useState([]); // [{ id, agentId, agentName, agentIcon, messages }]
  const [activeTabId, setActiveTabId] = useState(null);

  // Mensagem sendo digitada
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Controle de scroll
  const [showScrollDown, setShowScrollDown] = useState(false);

  // Busca no histórico
  const [searchHistory, setSearchHistory] = useState('');

  // Busca de agentes
  const [searchAgents, setSearchAgents] = useState('');

  // Redimensionamento da sidebar
  const [sidebarWidthPct, setSidebarWidthPct] = useState(20); // % inicial
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  // Ref para scroll automático
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);


  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================

  const getCompanyId = () => {
    try {
      const stored = localStorage.getItem('company_id');
      if (stored) return stored;
      if (user?.user_metadata?.company_id) return user.user_metadata.company_id;
      if (user?.company_id) return user.company_id;
      return null;
    } catch {
      return null;
    }
  };

  // ============================================================================
  // CARREGAR AGENTES
  // ============================================================================

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const companyId = getCompanyId();
      
      if (!companyId) {
        console.warn('[HaloAgents] Company ID não encontrado!');
        return;
      }

      const { data, error } = await supabase
        .from('agentes')
        .select('*')
        .eq('empresa_id', companyId)
        .order('nome', { ascending: true });

      if (error) {
        console.error('[HaloAgents] Erro Supabase:', error);
        throw error;
      }
      
      setAgents(data || []);
    } catch (err) {
      console.error('[HaloAgents] Erro ao carregar agentes:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  // ============================================================================
  // CARREGAR HISTÓRICO DE CONVERSAS
  // ============================================================================

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const companyId = getCompanyId();
      if (!companyId || !user?.id) return;

      // Buscar conversas do usuário
      const { data, error } = await supabase
        .from('halo_conversations')
        .select(`
          *,
          agentes:agente_id (
            id,
            nome,
            modelo,
            descricao
          )
        `)
        .eq('empresa_id', companyId)
        .eq('usuario_id', user.id)
        .eq('status', 'ativa')
        .order('ultima_mensagem_em', { ascending: false, nullsFirst: false })
        .order('criado_em', { ascending: false });

      if (error) {
        console.error('[HaloAgents] Erro ao carregar conversas:', error);
        return;
      }

      setConversations(data || []);
    } catch (err) {
      console.error('[HaloAgents] Erro ao carregar conversas:', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  // ============================================================================
  // FUNÇÕES DE CHAT
  // ============================================================================

  /**
   * Busca ou cria uma conversa com o agente
   */
  const getOrCreateConversation = async (agentId) => {
    try {
      const companyId = getCompanyId();
      if (!companyId || !user?.id) return null;

      // Verificar se já existe conversa ativa
      const { data: existing, error: errExisting } = await supabase
        .from('halo_conversations')
        .select('*')
        .eq('agente_id', agentId)
        .eq('usuario_id', user.id)
        .eq('status', 'ativa')
        .maybeSingle();

      if (errExisting) {
        console.error('[HaloAgents] Erro ao buscar conversa:', errExisting);
        // Não retornar null, continuar para criar nova conversa
      }

      if (existing) {
        return existing;
      }

      // Criar nova conversa
      const { data: newConv, error: errCreate } = await supabase
        .from('halo_conversations')
        .insert({
          empresa_id: companyId,
          agente_id: agentId,
          usuario_id: user.id,
          canal: 'interno',
          status: 'ativa'
        })
        .select()
        .single();

      if (errCreate) {
        console.error('[HaloAgents] Erro ao criar conversa:', errCreate);
        throw errCreate;
      }

      return newConv;

    } catch (error) {
      console.error('[HaloAgents] Erro em getOrCreateConversation:', error);
      return null;
    }
  };

  /**
   * Carrega mensagens de uma conversa
   */
  const loadMessages = async (conversaId) => {
    try {
      const { data, error } = await supabase
        .from('halo_messages')
        .select('*')
        .eq('conversa_id', conversaId)
        .is('excluido_em', null)
        .order('criado_em', { ascending: true });

      if (error) {
        console.error('[HaloAgents] Erro ao carregar mensagens:', error);
        return [];
      }

      return data || [];

    } catch (error) {
      console.error('[HaloAgents] Erro em loadMessages:', error);
      return [];
    }
  };

  /**
   * Envia mensagem para o agente
   */
  const sendMessage = async (tabId, mensagem) => {
    if (!mensagem.trim()) return;

    const tab = openTabs.find(t => t.id === tabId);
    if (!tab) return;

    setSending(true);
    setInputMessage('');

    try {
      // Se não tem conversationId, criar conversa primeiro
      let conversationId = tab.conversationId;
      if (!conversationId) {
        const conversation = await getOrCreateConversation(tab.agentId);
        if (!conversation) {
          throw new Error('Não foi possível criar a conversa');
        }
        conversationId = conversation.id;

        // Atualizar tab com conversationId
        setOpenTabs(prev => prev.map(t =>
          t.id === tabId ? { ...t, conversationId } : t
        ));
      }

      // Adicionar mensagem do usuário na UI imediatamente
      const userMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        conteudo: mensagem,
        criado_em: new Date().toISOString()
      };

      setOpenTabs(prev => prev.map(t =>
        t.id === tabId
          ? { ...t, messages: [...t.messages, userMessage] }
          : t
      ));

      // Pegar token do Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão não encontrada');
      }

      // Enviar para backend
      const response = await fetch(API_ENDPOINTS.HALO_CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          conversaId: conversationId,
          agenteId: tab.agentId,
          mensagem: mensagem
        })
      });

      // Verificar se é JSON antes de fazer parse
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('[HaloAgents] Resposta não é JSON:', text.substring(0, 500));
        throw new Error(`Servidor retornou ${response.status}: ${text.substring(0, 100)}`);
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao enviar mensagem');
      }

      // Log de créditos (debug)
      // if (data.creditos) {
      //   console.log('[HaloAgents] Créditos:', {
      //     descontados: data.creditos.descontados,
      //     saldo_restante: data.creditos.saldo_restante,
      //     custo_usd: data.creditos.custo_usd,
      //     sucesso: data.creditos.sucesso
      //   });
      // } else {
      //   console.warn('[HaloAgents] Nenhuma informação de créditos retornada');
      // }

      // Adicionar resposta do agente na UI
      const agentMessage = {
        id: data.mensagemId,
        role: 'assistant',
        conteudo: data.conteudo,
        criado_em: new Date().toISOString(),
        metadados: data.metadados
      };

      setOpenTabs(prev => prev.map(t =>
        t.id === tabId
          ? { ...t, messages: [...t.messages, agentMessage] }
          : t
      ));

    } catch (error) {
      console.error('[HaloAgents] Erro ao enviar mensagem:', error);
      alert(`Erro ao enviar mensagem: ${error.message}`);

      // Remover mensagem temporária do usuário em caso de erro
      setOpenTabs(prev => prev.map(t =>
        t.id === tabId
          ? { ...t, messages: t.messages.filter(m => !m.id.startsWith('temp-')) }
          : t
      ));
    } finally {
      setSending(false);
    }
  };

  // ============================================================================
  // ABRIR NOVA CONVERSA COM AGENTE
  // ============================================================================

  const openNewChat = async (agent) => {
    // Verificar se já existe aba aberta para este agente
    const existing = openTabs.find(tab => tab.agentId === agent.id);
    if (existing) {
      setActiveTabId(existing.id);
      return;
    }

    try {
      // Buscar ou criar conversa
      const conversation = await getOrCreateConversation(agent.id);
      
      // Carregar mensagens se a conversa já existe
      let messages = [];
      if (conversation) {
        messages = await loadMessages(conversation.id);
      }

      // Criar nova aba
      const newTab = {
        id: `tab-${Date.now()}`,
        agentId: agent.id,
        agentName: agent.nome || 'Agente',
        agentIcon: getAgentIcon(agent),
        messages: messages,
        conversationId: conversation?.id || null,
        favorito: conversation?.favorito || false,
      };

      setOpenTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);

    } catch (error) {
      console.error('[HaloAgents] Erro ao abrir chat:', error);
      alert('Erro ao abrir conversa com o agente');
    }
  };

  // ============================================================================
  // ABRIR CONVERSA EXISTENTE DO HISTÓRICO
  // ============================================================================

  const openExistingConversation = async (conversation) => {
    try {
      // Verificar se já existe aba aberta para esta conversa
      const existing = openTabs.find(tab => tab.conversationId === conversation.id);
      if (existing) {
        setActiveTabId(existing.id);
        return;
      }

      // Buscar agente
      const agent = agents.find(a => a.id === conversation.agente_id);
      if (!agent) {
        console.error('[HaloAgents] Agente não encontrado');
        return;
      }

      // Carregar mensagens
      const messages = await loadMessages(conversation.id);

      // Criar nova aba
      const newTab = {
        id: `tab-${Date.now()}`,
        agentId: agent.id,
        agentName: agent.nome || 'Agente',
        agentIcon: getAgentIcon(agent),
        messages: messages,
        conversationId: conversation.id,
        favorito: conversation.favorito || false,
      };

      setOpenTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);

    } catch (error) {
      console.error('[HaloAgents] Erro ao abrir conversa:', error);
      alert('Erro ao abrir conversa');
    }
  };

  // ============================================================================
  // FECHAR ABA
  // ============================================================================

  const closeTab = (tabId) => {
    setOpenTabs(prev => {
      const filtered = prev.filter(tab => tab.id !== tabId);
      // Se fechou a aba ativa, selecionar a última
      if (activeTabId === tabId && filtered.length > 0) {
        setActiveTabId(filtered[filtered.length - 1].id);
      } else if (filtered.length === 0) {
        setActiveTabId(null);
      }
      return filtered;
    });
  };

  // ============================================================================
  // AÇÕES DA CONVERSA
  // ============================================================================

  /**
   * Excluir conversa
   */
  const handleDeleteConversation = async (tabId) => {
    const tab = openTabs.find(t => t.id === tabId);
    if (!tab) return;

    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir esta conversa com ${tab.agentName}?\n\nEsta ação não pode ser desfeita.`
    );

    if (!confirmDelete) return;

    try {
      // Se tiver conversationId, deletar mensagens e conversa do banco
      if (tab.conversationId) {
        // 1. Deletar todas as mensagens em halo_messages
        const { error: haloMessagesError } = await supabase
          .from('halo_messages')
          .delete()
          .eq('conversa_id', tab.conversationId);

        if (haloMessagesError) throw haloMessagesError;

        // 2. Deletar todas as mensagens em mensagens (tabela de WhatsApp)
        const { error: mensagensError } = await supabase
          .from('mensagens')
          .delete()
          .eq('conversa_id', tab.conversationId);

        if (mensagensError) throw mensagensError;

        // 3. Deletar a conversa
        const { error: conversationError } = await supabase
          .from('halo_conversations')
          .delete()
          .eq('id', tab.conversationId);

        if (conversationError) throw conversationError;
      }

      // Fechar aba
      closeTab(tabId);

      // Recarregar histórico
      loadConversations();

    } catch (error) {
      console.error('[HaloAgents] Erro ao excluir conversa:', error);
      alert('Erro ao excluir conversa. Tente novamente.');
    }
  };

  /**
   * Toggle favorito
   */
  const handleToggleFavorite = async (tabId) => {
    const tab = openTabs.find(t => t.id === tabId);
    if (!tab || !tab.conversationId) return;

    try {
      const newFavoritoStatus = !tab.favorito;

      // Atualizar no banco
      const { error } = await supabase
        .from('halo_conversations')
        .update({ favorito: newFavoritoStatus })
        .eq('id', tab.conversationId);

      if (error) throw error;

      // Atualizar estado local
      setOpenTabs(prev => prev.map(t =>
        t.id === tabId
          ? { ...t, favorito: newFavoritoStatus }
          : t
      ));

      // Recarregar histórico
      loadConversations();

    } catch (error) {
      console.error('[HaloAgents] Erro ao favoritar conversa:', error);
      alert('Erro ao favoritar conversa. Tente novamente.');
    }
  };

  /**
   * Exportar conversa para PDF
   */
  const handleExportPDF = async (tabId) => {
    const tab = openTabs.find(t => t.id === tabId);
    if (!tab) return;

    if (!tab.messages || tab.messages.length === 0) {
      alert('Não há mensagens para exportar nesta conversa.');
      return;
    }

    try {
      // Importar jsPDF dinamicamente
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Configurações
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let y = margin;

      // Título
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(`Conversa com ${tab.agentName}`, margin, y);
      y += 10;

      // Data
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      doc.text(new Date().toLocaleString('pt-BR'), margin, y);
      y += 15;

      // Linha separadora
      doc.setDrawColor(180, 59, 234);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;

      // Mensagens
      doc.setTextColor(0);
      tab.messages.forEach((msg, index) => {
        // Verificar se precisa de nova página
        if (y > pageHeight - 40) {
          doc.addPage();
          y = margin;
        }

        // Nome do remetente
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        const sender = msg.role === 'user' ? 'Você' : tab.agentName;
        doc.text(sender + ':', margin, y);
        y += 6;

        // Mensagem
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(msg.conteudo, maxWidth);
        doc.text(lines, margin, y);
        y += (lines.length * 5) + 8;

        // Hora
        doc.setFontSize(8);
        doc.setTextColor(150);
        const time = new Date(msg.criado_em).toLocaleString('pt-BR');
        doc.text(time, margin, y);
        doc.setTextColor(0);
        y += 10;
      });

      // Rodapé
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Página ${i} de ${totalPages} - Gerado em ${new Date().toLocaleString('pt-BR')}`,
          margin,
          pageHeight - 10
        );
      }

      // Download
      const filename = `conversa-${tab.agentName.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
      doc.save(filename);

    } catch (error) {
      console.error('[HaloAgents] Erro ao exportar PDF:', error);
      alert('Erro ao exportar PDF. Verifique se a biblioteca jsPDF está instalada.');
    }
  };

  // ============================================================================
  // ÍCONE DO AGENTE
  // ============================================================================

  const getAgentIcon = (agent) => {
    // Baseado no tipo/modelo do agente
    if (agent.modelo === 'nano') return 'speed';
    if (agent.modelo === 'super') return 'auto_awesome';
    if (agent.funcao_principal === 'Atendimento') return 'support_agent';
    if (agent.funcao_principal === 'Suporte Interno') return 'psychology';
    return 'smart_toy';
  };

  // ============================================================================
  // REDIMENSIONAMENTO DA SIDEBAR
  // ============================================================================

  const onResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      let pct = (x / rect.width) * 100;
      // Limites entre 15% e 50%
      pct = Math.max(15, Math.min(50, pct));
      setSidebarWidthPct(pct);
    };
    const onUp = () => {
      setIsResizing(false);
      // Persistir preferência do usuário
      try { localStorage.setItem('halo_agents_sidebar_width_pct', String(sidebarWidthPct)); } catch (_) {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // Evita seleção de texto durante o arraste
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizing, sidebarWidthPct]);

  // ============================================================================
  // FUNÇÕES DE SCROLL
  // ============================================================================

  const scrollToBottom = (smooth = true) => {
    try {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
      } else if (messagesContainerRef.current) {
        const el = messagesContainerRef.current;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      }
    } catch (_) {}
  };

  const checkScrollPosition = () => {
    try {
      const el = messagesContainerRef.current;
      if (!el) return;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
      setShowScrollDown(!nearBottom);
    } catch (_) {}
  };

  // ============================================================================
  // EFEITOS
  // ============================================================================

  useEffect(() => {
    // Carregar largura salva
    try {
      const saved = localStorage.getItem('halo_agents_sidebar_width_pct');
      if (saved) setSidebarWidthPct(parseFloat(saved));
    } catch (_) {}
    
    loadAgents();
    loadConversations();
  }, [user]);

  // Auto-scroll ao trocar de aba ou receber mensagens
  useEffect(() => {
    const activeTab = openTabs.find(tab => tab.id === activeTabId);
    if (activeTab && activeTab.messages && activeTab.messages.length > 0) {
      setTimeout(() => {
        scrollToBottom(false); // false = sem animação para ser instantâneo
      }, 300);
    }
  }, [activeTabId, openTabs]);

  // Auto-scroll quando novas mensagens chegam
  useEffect(() => {
    try {
      const el = messagesContainerRef.current;
      const activeTab = openTabs.find(tab => tab.id === activeTabId);
      if (el && activeTab) {
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
        if (nearBottom) {
          setTimeout(() => scrollToBottom(true), 50);
        }
      }
    } catch (_) {}
  }, [openTabs.find(tab => tab.id === activeTabId)?.messages?.length]);

  // ============================================================================
  // FILTRAR HISTÓRICO E AGENTES
  // ============================================================================

  const filteredConversations = conversations.filter(conv => 
    conv.agentes?.nome?.toLowerCase().includes(searchHistory.toLowerCase()) ||
    conv.ultima_mensagem?.toLowerCase().includes(searchHistory.toLowerCase())
  );

  const filteredAgents = agents.filter(agent => 
    agent.nome?.toLowerCase().includes(searchAgents.toLowerCase()) ||
    agent.funcao_principal?.toLowerCase().includes(searchAgents.toLowerCase()) ||
    agent.modelo?.toLowerCase().includes(searchAgents.toLowerCase())
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loadingAgents || loadingConversations) {
    return (
      <div className="halo-agents-container">
        <LoadingSpinner message="Carregando agentes..." />
      </div>
    );
  }

  const activeTab = openTabs.find(tab => tab.id === activeTabId);

  return (
    <div className="halo-agents-container" ref={containerRef}>
      {/* SIDEBAR - Histórico de conversas */}
      <aside className="halo-sidebar" style={{ width: `${sidebarWidthPct}%`, minWidth: '200px' }}>
        <div className="halo-sidebar-header">
          <h3>
            <AppIcon name="history" />
            Histórico
          </h3>
        </div>

        <div className="halo-sidebar-search">
          <input
            type="text"
            placeholder="Buscar conversas..."
            value={searchHistory}
            onChange={(e) => setSearchHistory(e.target.value)}
          />
        </div>

        <div className="halo-sidebar-list">
          {filteredConversations.length === 0 && (
            <div className="halo-empty-state">
              <AppIcon name="chat_bubble_outline" />
              <p>Nenhuma conversa ainda</p>
            </div>
          )}

          {filteredConversations.map(conv => {
            const agente = conv.agentes;
            const agenteIcon = agente?.modelo === 'nano' ? 'speed' : 'smart_toy';
            
            return (
              <div 
                key={conv.id} 
                className="halo-history-item"
                onClick={() => openExistingConversation(conv)}
                style={{ cursor: 'pointer' }}
              >
                <div className="halo-history-icon">
                  <AppIcon name={agenteIcon} />
                </div>
                <div className="halo-history-info">
                  <strong>{agente?.nome || 'Agente'}</strong>
                  <p>{conv.ultima_mensagem || 'Sem mensagens'}</p>
                </div>
                <div className="halo-history-time">
                  {conv.ultima_mensagem_em 
                    ? new Date(conv.ultima_mensagem_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                    : new Date(conv.criado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                  }
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* DIVISOR ARRASTÁVEL */}
      <div
        className="halo-resizer"
        onMouseDown={onResizeMouseDown}
        title="Arraste para redimensionar"
        style={{
          width: '6px',
          cursor: 'col-resize',
          background: isResizing ? 'rgba(68, 225, 255, 0.3)' : 'transparent',
          transition: 'background 0.15s ease',
          flexShrink: 0,
        }}
      />

      {/* ÁREA PRINCIPAL */}
      <main className="halo-main" style={{ flex: 1, minWidth: 0 }}>
        {/* ABAS DE CONVERSAS ABERTAS */}
        {openTabs.length > 0 && (
          <div className="settings-tabs">
            {openTabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-button ${activeTabId === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTabId(tab.id)}
                type="button"
              >
                <AppIcon name={tab.agentIcon} style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                {tab.agentName}
                <AppIcon name="close" style={{
                    fontSize: '1.1rem',
                    marginLeft: '0.75rem',
                    opacity: 0.7,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s'
                  }} />
              </button>
            ))}
          </div>
        )}

        {/* CONTEÚDO */}
        <div className="halo-content">
          {!activeTab ? (
            // CARDS DE AGENTES (quando nenhuma conversa está ativa)
            <div className="halo-agents-grid">
              <div className="halo-agents-header">
                <h2>Selecione um agente para iniciar uma conversa</h2>
                <div className="halo-agents-search">
                  <AppIcon name="search" />
                  <input
                    type="text"
                    placeholder="Buscar agentes..."
                    value={searchAgents}
                    onChange={(e) => setSearchAgents(e.target.value)}
                  />
                </div>
              </div>
              <div className="halo-agents-cards">
                {filteredAgents.map(agent => (
                  <div
                    key={agent.id}
                    className="halo-agent-card"
                    onClick={() => openNewChat(agent)}
                  >
                    <div className="halo-agent-icon">
                      <AppIcon name={getAgentIcon(agent)} />
                    </div>
                    <h3>{agent.nome}</h3>
                    <p className="halo-agent-type">
                      {agent.modelo === 'nano' && '⚡ Halo Nano'}
                      {agent.modelo === 'super' && '✨ Halo Tools'}
                      {!agent.modelo && '🤖 Agente'}
                    </p>
                    <p className="halo-agent-function">{agent.funcao_principal || 'Assistente'}</p>
                  </div>
                ))}

                {filteredAgents.length === 0 && agents.length > 0 && (
                  <div className="halo-empty-state">
                    <AppIcon name="search_off" />
                    <p>Nenhum agente encontrado</p>
                    <small>Tente outro termo de busca</small>
                  </div>
                )}

                {agents.length === 0 && (
                  <div className="halo-empty-state">
                    <AppIcon name="smart_toy" />
                    <p>Nenhum agente disponível</p>
                    <small>Crie um agente em "Agentes Halo"</small>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // ÁREA DE CHAT
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Sidebar de ações */}
              <ChatActionsSidebar
                conversationId={activeTab.id}
                isFavorited={activeTab.favorito || false}
                onDelete={() => handleDeleteConversation(activeTab.id)}
                onToggleFavorite={() => handleToggleFavorite(activeTab.id)}
                onExportPDF={() => handleExportPDF(activeTab.id)}
              />

              <div 
                className="halo-chat-messages" 
                ref={messagesContainerRef}
                onScroll={checkScrollPosition}
                style={{ marginRight: '50px' }}
              >
                {activeTab.messages.length === 0 && (
                  <div className="halo-chat-welcome">
                    <div className="halo-welcome-icon">
                      <AppIcon name={activeTab.agentIcon} />
                    </div>
                    <h3>Conversa com {activeTab.agentName}</h3>
                    <p>Digite uma mensagem para começar</p>
                  </div>
                )}

                {activeTab.messages.map(msg => (
                  <div key={msg.id} className={`halo-message ${msg.role}`}>
                    <div className="halo-message-avatar">
                      <AppIcon name={msg.role === 'user' ? 'person' : activeTab.agentIcon} />
                    </div>
                    <div className="halo-message-content">
                      <div className="halo-message-text">
                        {msg.conteudo.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < msg.conteudo.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="halo-message-time">
                        {new Date(msg.criado_em).toLocaleTimeString('pt-BR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                <div ref={messagesEndRef} />

                {/* Botão de scroll para baixo */}
                {showScrollDown && (
                  <div style={{ 
                    position: 'sticky', 
                    bottom: '14px', 
                    zIndex: 5, 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    pointerEvents: 'none' 
                  }}>
                    <button
                      type="button"
                      onClick={() => scrollToBottom(true)}
                      title="Ir para a última mensagem"
                      aria-label="Ir para a última mensagem"
                      style={{
                        pointerEvents: 'auto',
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        border: '2px solid rgba(180, 59, 234, 0.5)',
                        background: 'linear-gradient(135deg, rgba(180, 59, 234, 0.9), rgba(68, 225, 255, 0.9))',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        transition: 'all 0.2s ease',
                        marginRight: '1rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(180, 59, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                    >
                      <AppIcon name="arrow_downward" style={{ fontSize: '24px' }} />
                    </button>
                  </div>
                )}
              </div>

              <div className="halo-chat-input">
                <input
                  type="text"
                  placeholder={`Mensagem para ${activeTab.agentName}...`}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !sending && inputMessage.trim()) {
                      sendMessage(activeTab.id, inputMessage);
                    }
                  }}
                  disabled={sending}
                />
                <button
                  className="halo-send-button"
                  onClick={() => sendMessage(activeTab.id, inputMessage)}
                  disabled={!inputMessage.trim() || sending}
                >
                  <AppIcon name={sending ? 'hourglass_empty' : 'send'} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
